import { ApolloError, gql, useQuery } from '@apollo/client';
import React from 'react';
import { Redirect, useRouteMatch } from 'react-router-dom';
import { Outlet } from 'react-router-dom-v5-compat';

import { Loading } from 'src/components/common/loading/Loading';
import { NoBillingEmailBanner } from 'src/components/noBillingEmailBanner/NoBillingEmailBanner';
import { NoVerifiedEmailBanner } from 'src/components/noVerifiedEmailBanner/NoVerifiedEmailBanner';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { usePermissions } from 'src/components/permissionGuards/usePermissions';
import { PublicVariantPreviewBanner } from 'src/components/publicVariantPreviewBanner/PublicVariantPreviewBanner';
import { useAuth } from 'src/context/AuthContext';
import { isUserIdentity } from 'src/hooks/useIdentity';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import type {
  AppLayoutQuery,
  AppLayoutQueryVariables,
} from 'src/lib/graphqlTypes/types';
import { BillingPlanTier, SubscriptionState } from 'src/lib/graphqlTypes/types';
import { useLDFlag } from 'src/lib/launchDarkly';
import { DocsRouter } from 'src/lib/routers';

import { invite } from '../account/routes';
import { AppModals } from '../appModals/AppModals';
import * as routes from '../onboarding/routes';

import {
  EOLBanner,
  eolBannerFragment as EOLBannerFragment,
} from './components/eolBanner/EOLBanner';
import { SamlCertBanner } from './components/samlCertBanner/SamlCertBanner';
import { ScheduledMaintenances } from './components/scheduledMaintenances/ScheduledMaintenances';
import { TrialAlertBanner } from './components/trialAlertBanner/TrialAlertBanner';

const appLayoutQuery = gql<
  AppLayoutQuery,
  AppLayoutQueryVariables
>/* GraphQL */ `
  query AppLayoutQuery($accountId: ID!, $canQueryBilling: Boolean!) {
    account(id: $accountId) {
      id
      isLocked
      currentSubscription @include(if: $canQueryBilling) {
        state
        uuid
        ...EOLBannerFragment
      }
      currentPlan {
        tier
        isTrial
        id
        readableId
      }
      ssoV2 {
        currentConnection {
          id
          __typename
        }
      }
    }
  }
  ${EOLBannerFragment}
`;

export const AppLayout = () => {
  const { user, account, loading, error } = useAuth();
  const currentAccountId = account?.id;

  const { data: permissionsData } = usePermissions(
    currentAccountId,
    undefined,
    undefined,
    !currentAccountId,
  );
  const canQueryBilling =
    !!permissionsData?.accountPermissions?.canQueryBillingInfo;

  const { data, loading: accountLoading } = useQuery(appLayoutQuery, {
    variables: {
      accountId: currentAccountId || '',
      canQueryBilling,
    },
    skip: !currentAccountId,
    fetchPolicy: 'network-only',
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });

  const shouldShowSelfServeSamlRotationUI = useLDFlag(
    'tnt-self-service-saml-rotation',
  );

  const currentSubscription = data?.account?.currentSubscription;
  const currentPlan = data?.account?.currentPlan;
  const isInviteRoute = !!useRouteMatch(invite.definition);
  const isWelcomeRoute = !!useRouteMatch(routes.welcome.definition);
  const isSelectPlanRoute = !!useRouteMatch(routes.selectPlan.definition);
  const isCheckoutRoute = !!useRouteMatch(routes.checkout.definition);

  const isSelfServeOnboardingRoute = isSelectPlanRoute || isCheckoutRoute;

  const hasSamlConnection =
    data?.account?.ssoV2?.currentConnection?.__typename === 'SamlConnection';

  // try to figure out if this is a network error
  if (
    error &&
    !(
      error instanceof ApolloError &&
      error.networkError?.message &&
      // different browsers have different messages for network errors
      [
        'Failed to fetch',
        'NetworkError when attempting to fetch resource.',
      ].includes(error.networkError.message)
    )
  ) {
    // was not a network error / an unforeseen error has occurred
    throw error;
  }

  if (loading || accountLoading) return <Loading />;
  // if not an invite or welcome route, and the user is logged in, and the user is a user identity
  if (!isInviteRoute && !isWelcomeRoute && user && isUserIdentity(user)) {
    // if the user is a user identity, and the user has not updated their email or full name redirect to the welcome route
    if (
      (!user.email && user.canUpdateEmail) ||
      (!user.fullName && user.canUpdateFullName)
    ) {
      return <Redirect to={routes.welcome.location({})} />;
    }

    if (!data?.account?.isLocked) {
      // TODO: remove check for SubscriptionState.PENDING
      // https://apollographql.atlassian.net/browse/ONB-933
      if (
        (currentAccountId && !currentPlan) ||
        currentPlan?.tier === BillingPlanTier.NONE ||
        (currentSubscription?.state === SubscriptionState.PENDING &&
          currentPlan?.tier === BillingPlanTier.SCALE)
      ) {
        return (
          <Redirect
            to={routes.selectPlan.location({
              planId: currentPlan?.readableId,
              accountId: currentAccountId,
              type: 'signup', // TODO: figure out a way of knowing if this should be signup or upgrade
            })}
          />
        );
      }
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-full flex-col">
        <AppModals />
        {currentAccountId && !data?.account?.isLocked && (
          <PermissionGuard
            accountId={currentAccountId}
            accountPermissions="canQueryBillingInfo"
            fallback={null}
          >
            <NoBillingEmailBanner accountId={currentAccountId} />
            {currentPlan?.isTrial && (
              <TrialAlertBanner
                accountId={currentAccountId}
                learnMoreHref={DocsRouter.path('EnterpriseTrialOrgPlan')}
              />
            )}
          </PermissionGuard>
        )}
        {data?.account?.currentSubscription && (
          <EOLBanner subscription={data.account.currentSubscription} />
        )}
        {user && !isSelfServeOnboardingRoute && <NoVerifiedEmailBanner />}
        {shouldShowSelfServeSamlRotationUI &&
          hasSamlConnection &&
          currentAccountId &&
          !data?.account?.isLocked && (
            <SamlCertBanner accountId={currentAccountId} />
          )}
        <PublicVariantPreviewBanner />
        <ScheduledMaintenances />
        <Outlet />
      </div>
    </div>
  );
};
